import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, FileText, Loader2, Building2, AlertCircle, Search, ChevronRight, Filter, ImageIcon, Star, Phone, Mail, UserRound, Briefcase, Eye, EyeOff, Calculator, ShieldCheck, Clock, Box, Trash2, X } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import PhotoSearchModal from "../components/PhotoSearchModal";
import { Models3DDrawer, FavoritesDrawer } from "./MagicSearchPage";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Color keywords for auto-extraction (IT + EN). Single source of truth.
const COLOR_KEYWORDS = [
  "rosso", "rossa", "blu", "verde", "giallo", "gialla", "nero", "nera",
  "bianco", "bianca", "beige", "grigio", "grigia", "marrone", "rosa",
  "viola", "arancione", "tortora", "panna", "avorio", "ottone", "oro",
  "argento", "noce", "rovere", "cromato", "antracite", "sabbia", "tabacco",
  "bordeaux", "ocra", "salvia",
  // EN fallback (a volte gli admin scrivono in EN)
  "red", "blue", "green", "yellow", "black", "white", "grey", "gray", "brown",
];

function extractColorFromQuery(query) {
  if (!query) return "";
  const tokens = query.toLowerCase().split(/[\s,;]+/).filter(Boolean);
  for (const t of tokens) {
    if (COLOR_KEYWORDS.includes(t)) return t;
  }
  return "";
}

/**
 * Preventivatore (admin only): doppia ricerca in parallelo su TUTTI i PDF di
 * tutti i fornitori.
 *
 *  - COLONNA SINISTRA · AI (Gemini 3 Flash): interpreta query in linguaggio
 *    naturale, applica brand-detection, ranking con priorità ai page-hit dove
 *    TUTTI i token compaiono insieme. Restituisce top 5 con score e reasoning.
 *
 *  - COLONNA DESTRA · TRADIZIONALE (zero AI): stesso motore della Magic Search
 *    dei fornitori ma su TUTTI i listini. AND-logic: tutti i token DEVONO
 *    apparire nella stessa pagina. Restituisce fino a 20 risultati.
 *
 *  Tutti i risultati (entrambe le colonne) aprono il viewer PDF.js interno alla
 *  pagina giusta con highlight del primo token utile (post brand-removal).
 */
export default function Preventivatore() {
  const [query, setQuery] = useState("");
  const [loadingAI, setLoadingAI] = useState(false);
  const [loadingTrad, setLoadingTrad] = useState(false);
  const [aiResp, setAiResp] = useState(null);
  const [tradResp, setTradResp] = useState(null);
  const [error, setError] = useState("");
  // Photo search modal state — null = chiusa, oggetto con dati = aperta
  const [photoModal, setPhotoModal] = useState(null);
  // Auto-photo-search (strip in alto): viene triggerata insieme ad AI+Trad
  // per dare un colpo d'occhio immediato delle foto trovate per la query.
  const [topPhotos, setTopPhotos] = useState({ loading: false, items: [], error: "" });
  // Contatti del fornitore rilevato (agente + referente aziendale) — admin only
  const [brandContacts, setBrandContacts] = useState(null);
  // Preferiti / Storico / Drawer 3D (come Magic Search)
  const [favorites, setFavorites] = useState([]);
  const [favOpen, setFavOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [drawer3d, setDrawer3d] = useState(null); // { supplierId, supplierName }

  const favMap = useMemo(() => {
    const m = new Map();
    for (const f of favorites) m.set(`${f.asset_id}:${f.page}`, f);
    return m;
  }, [favorites]);

  const fetchFavorites = useCallback(async () => {
    try { const { data } = await api.get("/magic-search/favorites"); setFavorites(data.favorites || []); } catch { /* silent */ }
  }, []);
  const fetchHistory = useCallback(async () => {
    try { const { data } = await api.get("/magic-search/history"); setHistory(data.history || []); } catch { /* silent */ }
  }, []);
  useEffect(() => { fetchFavorites(); fetchHistory(); }, [fetchFavorites, fetchHistory]);
  // Il visualizzatore PDF si apre in una nuova scheda: quando torno qui, ricarico
  // i preferiti così le pagine appena salvate con la stella compaiono subito.
  useEffect(() => {
    const onFocus = () => fetchFavorites();
    const onVis = () => { if (document.visibilityState === "visible") fetchFavorites(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => { window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onVis); };
  }, [fetchFavorites]);

  const toggleFavorite = useCallback(async (result, page) => {
    const key = `${result.asset_id}:${page}`;
    const existing = favMap.get(key);
    try {
      if (existing) {
        await api.delete(`/magic-search/favorites/${existing.id}`);
        setFavorites((prev) => prev.filter((f) => f.id !== existing.id));
      } else {
        const { data } = await api.post("/magic-search/favorites", {
          asset_id: result.asset_id,
          supplier_id: result.supplier_id || "",
          supplier_name: result.supplier_name || "",
          kind: result.kind || "",
          page,
          title: result.title || result.filename || "",
          query: query.trim(),
        });
        setFavorites((prev) => (prev.some((f) => f.id === data.id) ? prev : [data, ...prev]));
      }
    } catch { toast.error("Operazione non riuscita"); }
  }, [favMap, query]);

  const deleteHistory = useCallback(async (id) => {
    try { await api.delete(`/magic-search/history/${id}`); setHistory((prev) => prev.filter((h) => h.id !== id)); } catch { /* silent */ }
  }, []);

  const openPhotoSearch = useCallback((result) => {
    // Estrai colore dalla query originale dell'utente (es: "POLTRONA COCOON ROSSA")
    const autoColor = extractColorFromQuery(query);
    // Product name: usa il titolo del listino oppure i tokens AI normalizzati
    // (puliti dal brand). Il backend Brave gestisce la scope sul dominio fornitore.
    const tokensClean = (aiResp?.tokens || []).join(" ").trim();
    const productName = tokensClean || query.trim();
    setPhotoModal({
      productName,
      supplierId: result.supplier_id || "",
      supplierName: result.supplier_name || "",
      color: autoColor,
    });
  }, [query, aiResp]);

  const openTopPhoto = useCallback((idx) => {
    // Quando l'utente clicca su una miniatura della strip in alto, apriamo
    // il modal PhotoSearchModal con i dati della query corrente. Passiamo
    // i risultati pre-caricati come initialResults così il modal salta il
    // fetch e mostra subito la vista ingrandita.
    const autoColor = extractColorFromQuery(query);
    setPhotoModal({
      productName: query.trim(),
      supplierId: "",
      supplierName: "Ricerca generica",
      color: autoColor,
      initialIdx: idx,
      initialResults: topPhotos.items,
    });
  }, [query, topPhotos.items]);

  const runSearch = useCallback(async (qArg) => {
    const raw = typeof qArg === "string" ? qArg : query;
    const q = (raw || "").trim();
    if (q.length < 2) {
      setError("Inserisci almeno 2 caratteri");
      return;
    }
    if (typeof qArg === "string") setQuery(qArg);
    setError("");
    setAiResp(null);
    setTradResp(null);
    setLoadingAI(true);
    setLoadingTrad(true);
    setTopPhotos({ loading: true, items: [], error: "" });
    // Parallelo: AI + Tradizionale + Auto Photo Search (tutti indipendenti).
    // L'auto-photo-search usa la query raw SENZA scope fornitore: serve a
    // dare un colpo d'occhio veloce di cosa Brave trova. L'utente può poi
    // raffinare cliccando "Foto AI" sui singoli risultati (con scope sul
    // fornitore di quel listino).
    const autoColor = extractColorFromQuery(q);
    const aiPromise = api.post("/admin/preventivatore/search", { q })
      .then(({ data }) => { setAiResp(data); return data; })
      .catch((e) => {
        const msg = e.response?.data?.detail || e.message || "Errore AI";
        toast.error(`AI: ${msg}`);
        return null;
      })
      .finally(() => setLoadingAI(false));
    const tradPromise = api.post("/admin/preventivatore/traditional-search", { q })
      .then(({ data }) => { setTradResp(data); return data; })
      .catch((e) => {
        const msg = e.response?.data?.detail || e.message || "Errore ricerca";
        toast.error(`Tradizionale: ${msg}`);
        return null;
      })
      .finally(() => setLoadingTrad(false));
    // Attendiamo AI + Tradizionale per sapere QUALE fornitore è stato rilevato:
    // la ricerca foto parte SOLO sul suo sito ufficiale (il backend allarga al
    // web solo se il sito non offre almeno 3 immagini).
    const [aiData, tradData] = await Promise.all([aiPromise, tradPromise]);
    const detectedSupplierId = aiData?.brand_filter?.id || tradData?.brand_filter?.id || "";
    try {
      const { data } = await api.post("/preventivatore/photo-search", {
        product_name: q,
        supplier_id: detectedSupplierId,
        color: autoColor,
      });
      setTopPhotos({ loading: false, items: data.items || [], error: "" });
    } catch (e) {
      // Non blocchiamo la UX: la photo search fallita non è critica.
      const msg = e.response?.data?.detail || e.message || "Errore foto";
      setTopPhotos({ loading: false, items: [], error: msg });
    }
    try { await api.post("/magic-search/history", { q }); } catch { /* silent */ }
    fetchHistory();
  }, [query, fetchHistory]);

  const openPdf = useCallback((assetId, suggestedPage, sourceTokens) => {
    // Apre il viewer interno PDF.js alla pagina giusta. Usa come highlight il
    // primo token utile (post brand-removal) — fornito dal backend in `tokens`.
    const fileUrl = `${API}/assets/${assetId}/preview`;
    const qp = new URLSearchParams();
    qp.set("file", fileUrl);
    if (suggestedPage && Number.isFinite(Number(suggestedPage))) {
      qp.set("page", String(suggestedPage));
    }
    const highlightToken = (sourceTokens && sourceTokens[0])
      || (query.trim().split(/\s+/)[0] || "").toLowerCase();
    if (highlightToken) qp.set("search", highlightToken);
    // Preventivatore è admin-only → sempre admin=1 (mostra "Scarica" nel viewer)
    qp.set("admin", "1");
    qp.set("from", "preventivatore");
    window.open(`/pdfjs/viewer.html?${qp.toString()}`, "_blank");
    // === Self-tuning ranking: registra il click in modo silenzioso ===
    // Best-effort: nessun await, errori ignorati. Il sistema impara da quali
    // (asset, pagina) gli admin aprono davvero per una certa query.
    const q = query.trim();
    if (q && assetId && suggestedPage) {
      api.post("/admin/preventivatore/click", {
        q,
        asset_id: assetId,
        page: Number(suggestedPage),
      }).catch(() => {});
    }
  }, [query]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      runSearch();
    }
  };

  const loading = loadingAI || loadingTrad;
  const brandFilter = aiResp?.brand_filter || tradResp?.brand_filter;
  const brandId = brandFilter?.id || null;

  // Al riconoscimento del brand carica i contatti (agente + referente aziendale)
  useEffect(() => {
    if (!brandId) {
      setBrandContacts(null);
      return;
    }
    let cancelled = false;
    api.get(`/suppliers/${brandId}`)
      .then((r) => { if (!cancelled) setBrandContacts(r.data); })
      .catch(() => { if (!cancelled) setBrandContacts(null); });
    return () => { cancelled = true; };
  }, [brandId]);

  const hasAgent = Boolean(brandContacts?.agent_name || brandContacts?.agent_email || brandContacts?.agent_phone);
  const companyRefEmail = brandContacts?.orders_email || brandContacts?.contact_email || "";
  const hasCompanyRef = Boolean(brandContacts?.company_contact_name || companyRefEmail || brandContacts?.contact_phone);

  // Oggetto email ordine precompilato (corpo vuoto: lo scrive l'utente)
  const orderMailto = (email) => {
    // sanifica: rimuove spazi e prende solo il primo indirizzo se ce ne sono più d'uno
    const clean = String(email || "").trim().split(/[;,\s]+/)[0];
    const subject = `Richiesta ${brandFilter?.name || ""}`.trim();
    return `mailto:${clean}?subject=${encodeURIComponent(subject)}`;
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-28 pb-10 overflow-x-clip" data-testid="preventivatore-page">
      {/* Header */}
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-gold mb-2 inline-flex items-center gap-2">
            <Sparkles size={12} /> Admin tool · Doppia ricerca parallela
          </div>
          <h1 className="font-display font-light text-4xl lg:text-5xl text-ink tracking-tight">
            Preventivatore
          </h1>
        <p className="mt-3 text-slate1 text-base max-w-2xl leading-relaxed">
          Ricerca globale su TUTTI i listini PDF indicizzati: a sinistra l&apos;AI
          interpreta la query e propone i match più rilevanti; a destra la ricerca
          tradizionale (AND su tutte le parole nella stessa pagina) ti dà la
          lista completa senza filtro semantico.
        </p>
        </div>
        {/* Link rapidi in alto a dx */}
        <div className="flex items-center gap-2 pt-1">
          <button
            onClick={() => setFavOpen(true)}
            className="inline-flex items-center gap-2 px-3.5 py-2 border border-ink/20 rounded-sm text-[11px] tracking-[0.2em] uppercase text-ink hover:border-gold transition-colors"
            data-testid="prev-favorites-open"
            title="I tuoi listini/pagine salvati"
          >
            <Star size={13} className={favorites.length ? "text-gold fill-gold" : "text-slate1"} /> Preferiti{favorites.length ? ` (${favorites.length})` : ""}
          </button>
          <Link
            to="/admin/imtask?view=calculator"
            className="inline-flex items-center gap-2 px-3.5 py-2 border border-emerald-300 bg-emerald-50 text-emerald-800 rounded-sm text-[11px] tracking-[0.2em] uppercase hover:bg-emerald-100 transition-colors"
            data-testid="prev-calcolo-prezzo-link"
            title="Calcolo Prezzo con sconti automatici dai listini"
          >
            <Calculator size={13} /> Calcolo Prezzo
          </Link>
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 px-3.5 py-2 border border-ink/20 rounded-sm text-[11px] tracking-[0.2em] uppercase text-ink hover:bg-ink hover:text-cream transition-colors"
            data-testid="prev-back-admin-top"
            title="Torna alla console admin"
          >
            <ShieldCheck size={13} /> Admin
          </Link>
        </div>
      </div>

      {/* Search bar */}
      <div className="bg-white border border-ink/10 rounded-sm p-5 shadow-sm">
        <div className="flex items-center gap-3">
          <Search size={18} className="text-slate1 shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Es: tavolo allungabile cristallo rimadesio…"
            maxLength={200}
            disabled={loading}
            className="flex-1 min-w-0 bg-transparent border-0 focus:outline-none text-base text-ink placeholder:text-slate1/50"
            data-testid="preventivatore-query-input"
            autoFocus
          />
          <button
            onClick={runSearch}
            disabled={loading || query.trim().length < 2}
            className="inline-flex items-center gap-2 bg-ink text-cream px-5 py-2 text-xs tracking-[0.2em] uppercase rounded-sm hover:bg-ink/90 disabled:opacity-50 transition-colors"
            data-testid="preventivatore-search-btn"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {loading ? "Ricerca…" : "Cerca"}
          </button>
        </div>
        <div className="mt-2 text-[10px] tracking-[0.15em] uppercase text-slate1/70 flex items-center gap-4 flex-wrap">
          <span>AI: Gemini 3 Flash · re-rank semantico</span>
          <span>·</span>
          <span>Tradizionale: AND su token + brand auto-detect</span>
          <span>·</span>
          <span>Invio per cercare</span>
        </div>
      </div>

      {/* Storico ricerche */}
      {history.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="prev-history">
          <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-slate1">
            <Clock size={11} /> Recenti
          </span>
          {history.map((h) => (
            <span key={h.id} className="group inline-flex items-center gap-1 bg-white border border-ink/15 rounded-full pl-3 pr-1 py-1 text-xs text-ink hover:border-gold transition-colors" data-testid="prev-history-chip">
              <button onClick={() => runSearch(h.q)} disabled={loading} className="hover:text-gold transition-colors disabled:opacity-50" title="Rilancia questa ricerca">{h.q}</button>
              <button onClick={() => deleteHistory(h.id)} className="p-0.5 text-slate1/50 hover:text-rose-600 transition-colors" title="Rimuovi dallo storico" data-testid="prev-history-remove"><X size={12} /></button>
            </span>
          ))}
        </div>
      )}

      {error && (
        <div className="mt-4 bg-rose-50 border border-rose-300 text-rose-800 p-3 rounded-sm text-sm flex items-start gap-2" data-testid="preventivatore-error">
          <AlertCircle size={14} className="shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {/* Brand filter badge + contatti agente/referente (visible when query mentions a brand) */}
      {brandFilter && (
        <div className="mt-6 flex flex-wrap items-stretch gap-3" data-testid="preventivatore-brand-row">
          <div className="bg-ink/95 text-cream rounded-sm p-3 inline-flex items-center gap-2 text-xs self-start" data-testid="preventivatore-brand-filter">
            <Building2 size={13} className="text-gold" />
            <span className="text-[10px] tracking-[0.25em] uppercase text-gold">Brand rilevato</span>
            <span className="text-cream font-medium">{brandFilter.name}</span>
            <span className="text-cream/60 ml-1">— entrambe le ricerche ristrette a questo fornitore</span>
          </div>
          {(hasAgent || hasCompanyRef) && (
            <div className="bg-white border border-ink/10 rounded-sm px-4 py-2.5 flex flex-wrap items-center gap-x-6 gap-y-1.5 text-xs" data-testid="preventivatore-brand-contacts">
              {hasAgent && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-gold font-semibold">
                    <UserRound size={12} /> Agente
                  </span>
                  {brandContacts.agent_name && <span className="text-ink font-medium">{brandContacts.agent_name}</span>}
                  {brandContacts.agent_phone && (
                    <a href={`tel:${brandContacts.agent_phone}`} className="inline-flex items-center gap-1 text-slate1 hover:text-ink transition-colors" data-testid="brand-agent-phone">
                      <Phone size={11} /> {brandContacts.agent_phone}
                    </a>
                  )}
                  {brandContacts.agent_email && (
                    <a href={orderMailto(brandContacts.agent_email)} title="Email ordine all'agente: si apre il tuo programma di posta con l'oggetto precompilato" className="inline-flex items-center gap-1 text-slate1 hover:text-ink transition-colors" data-testid="brand-agent-email">
                      <Mail size={11} /> {brandContacts.agent_email}
                    </a>
                  )}
                </div>
              )}
              {hasAgent && hasCompanyRef && <span className="hidden md:block w-px h-5 bg-ink/10" />}
              {hasCompanyRef && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-gold font-semibold">
                    <Briefcase size={12} /> Referente aziendale
                  </span>
                  {brandContacts.company_contact_name && <span className="text-ink font-medium">{brandContacts.company_contact_name}</span>}
                  {brandContacts.contact_phone && (
                    <a href={`tel:${brandContacts.contact_phone}`} className="inline-flex items-center gap-1 text-slate1 hover:text-ink transition-colors" data-testid="brand-company-phone">
                      <Phone size={11} /> {brandContacts.contact_phone}
                    </a>
                  )}
                  {companyRefEmail && (
                    <a href={orderMailto(companyRefEmail)} title="Email ordine al fornitore: si apre il tuo programma di posta con l'oggetto precompilato" className="inline-flex items-center gap-1 text-slate1 hover:text-ink transition-colors" data-testid="brand-company-email">
                      <Mail size={11} /> {companyRefEmail}
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
          <button
            onClick={() => setDrawer3d({ supplierId: brandId, supplierName: brandFilter.name })}
            className="self-start inline-flex items-center gap-1.5 px-3 py-1.5 bg-gold/15 border border-gold/50 text-ink rounded-sm text-[10px] tracking-[0.2em] uppercase hover:bg-gold/25 transition-colors font-medium"
            data-testid="prev-3d-open"
          >
            <Box size={12} /> File 3D
          </button>
          {brandContacts?.notes && brandContacts.notes.trim() && (
            <div
              className="basis-full w-full text-[13px] font-semibold text-red-600 whitespace-nowrap overflow-hidden text-ellipsis"
              title={brandContacts.notes}
              data-testid="preventivatore-brand-notes"
            >
              {brandContacts.notes.replace(/\s*\n+\s*/g, "  ·  ").trim()}
            </div>
          )}
        </div>
      )}

      {/* Top Photo Strip · Auto Foto AI ─────────────────────────────────── */}
      {(topPhotos.loading || topPhotos.items.length > 0) && (
        <div className="mt-6 bg-white border border-ink/10 rounded-sm p-4" data-testid="top-photo-strip">
          <div className="flex items-center gap-2 mb-3">
            <ImageIcon size={13} className="text-gold" />
            <span className="text-[11px] tracking-[0.25em] uppercase text-slate1">Foto AI · colpo d&apos;occhio</span>
            {topPhotos.loading && (
              <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase text-slate1/70">
                <Loader2 size={10} className="animate-spin" /> ricerca in corso
              </span>
            )}
            {!topPhotos.loading && topPhotos.items.length > 0 && (
              <button
                onClick={() => openTopPhoto(0)}
                className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-gold hover:text-ink transition-colors"
                data-testid="top-photo-strip-expand"
              >
                Espandi e gestisci →
              </button>
            )}
          </div>
          {topPhotos.loading ? (
            <div className="grid grid-cols-3 gap-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="aspect-[4/3] bg-cream2 animate-pulse rounded-sm" />
              ))}
            </div>
          ) : topPhotos.items.length > 0 ? (
            <div className="grid grid-cols-3 gap-3">
              {topPhotos.items.slice(0, 3).map((it, i) => (
                <button
                  key={`${it.url}-${i}`}
                  type="button"
                  onClick={() => openTopPhoto(i)}
                  className="group relative aspect-[4/3] bg-cream2 border border-ink/10 rounded-sm overflow-hidden hover:border-gold transition-colors"
                  title={`${it.title || "Foto " + (i + 1)} · click per ingrandire e scaricare`}
                  data-testid={`top-photo-strip-thumb-${i}`}
                >
                  <img
                    src={it.thumbnail}
                    alt={it.title || `Foto ${i + 1}`}
                    className="w-full h-full object-contain transition-transform group-hover:scale-105"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      if (it.url && e.target.src !== it.url) e.target.src = it.url;
                    }}
                  />
                  <div className="absolute top-1.5 left-1.5 bg-ink/80 text-cream rounded-sm px-1.5 py-0.5 text-[9px] tracking-[0.15em] uppercase backdrop-blur-sm">
                    #{i + 1}
                  </div>
                  <div className="absolute top-1.5 right-1.5 bg-gold/95 text-ink rounded-sm px-1.5 py-0.5 text-[9px] tracking-[0.15em] uppercase font-medium backdrop-blur-sm inline-flex items-center gap-1">
                    <Star size={8} fill="currentColor" />
                    {Math.round(it.score)}
                  </div>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      )}

      {/* Two-column results */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Colonna SINISTRA: AI */}
        <ColumnAI
          loading={loadingAI}
          resp={aiResp}
          favMap={favMap}
          onToggleFav={toggleFavorite}
          on3D={(r) => setDrawer3d({ supplierId: r.supplier_id, supplierName: r.supplier_name })}
          onOpen={(r) => openPdf(r.asset_id, r.suggested_page, aiResp?.tokens)}
          onPhotoSearch={openPhotoSearch}
        />
        {/* Colonna DESTRA: Tradizionale */}
        <ColumnTraditional
          loading={loadingTrad}
          resp={tradResp}
          favMap={favMap}
          onToggleFav={toggleFavorite}
          on3D={(r) => setDrawer3d({ supplierId: r.supplier_id, supplierName: r.supplier_name })}
          onOpen={(r, page) => openPdf(r.asset_id, page, tradResp?.tokens)}
          onPhotoSearch={openPhotoSearch}
        />
      </div>

      {/* Quick link back to admin */}
      <div className="mt-12 text-center">
        <Link to="/admin" className="text-[11px] tracking-[0.25em] uppercase text-slate1 hover:text-ink transition-colors inline-flex items-center gap-1" data-testid="preventivatore-back-admin">
          ← Torna alla console admin
        </Link>
      </div>

      {/* Modal ricerca foto AI */}
      <PhotoSearchModal
        open={!!photoModal}
        productName={photoModal?.productName || ""}
        supplierId={photoModal?.supplierId || ""}
        supplierName={photoModal?.supplierName || ""}
        color={photoModal?.color || ""}
        initialIdx={typeof photoModal?.initialIdx === "number" ? photoModal.initialIdx : -1}
        initialResults={photoModal?.initialResults || null}
        onClose={() => setPhotoModal(null)}
      />

      {drawer3d && (
        <Models3DDrawer
          supplierId={drawer3d.supplierId}
          supplierName={drawer3d.supplierName}
          fallbackQuery={query}
          allowAsk={false}
          onClose={() => setDrawer3d(null)}
        />
      )}
      {favOpen && (
        <FavoritesDrawer
          favorites={favorites}
          onOpen={(f) => openPdf(f.asset_id, f.page, null)}
          onRemove={async (f) => {
            try { await api.delete(`/magic-search/favorites/${f.id}`); setFavorites((prev) => prev.filter((x) => x.id !== f.id)); }
            catch { toast.error("Rimozione non riuscita"); }
          }}
          onClose={() => setFavOpen(false)}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// COLONNA SINISTRA — AI re-rank
// ───────────────────────────────────────────────────────────────────────────
function ColumnAI({ loading, resp, onOpen, onPhotoSearch, favMap, onToggleFav, on3D }) {
  const aiPage = (r) => r.suggested_page || r.snippets?.[0]?.page || 1;
  return (
    <div className="min-w-0 bg-white border border-gold/40 rounded-sm overflow-hidden" data-testid="col-ai">
      {/* Header colonna */}
      <div className="bg-gold/10 border-b border-gold/30 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="inline-flex items-center gap-2">
            <Sparkles size={14} className="text-gold" />
            <span className="text-[11px] tracking-[0.25em] uppercase text-ink font-medium">Ricerca AI</span>
          </div>
          {resp?.candidates_count !== undefined && (
            <span className="text-[10px] tracking-[0.15em] uppercase text-slate1">
              Top {resp.results.length} su {resp.candidates_count} candidati
            </span>
          )}
        </div>
        <div className="text-[9px] tracking-[0.15em] uppercase text-slate1/70 mt-1">
          Re-rank semantico · privilegia pagine con TUTTI i token insieme
        </div>
      </div>

      <div className="p-4">
        {loading && <LoadingBlock label="AI in azione…" />}

        {/* AI reasoning */}
        {!loading && resp?.ai_reasoning && (
          <div className="bg-gold/5 border border-gold/30 rounded-sm p-3 mb-4" data-testid="preventivatore-ai-reasoning">
            <div className="text-[10px] tracking-[0.2em] uppercase text-gold mb-2 inline-flex items-center gap-1">
              <Sparkles size={11} /> Interpretazione AI
              {resp.is_uncertain && <span className="text-amber-600 ml-2">· Risultati incerti</span>}
            </div>
            <div className="text-xs text-ink leading-relaxed italic">{resp.ai_reasoning}</div>
            {resp.tokens && resp.tokens.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {resp.tokens.map((tok) => (
                  <span key={tok} className="text-[9px] tracking-[0.15em] uppercase bg-white border border-ink/10 px-1.5 py-0.5 rounded-sm text-slate1">
                    {tok}
                  </span>
                ))}
              </div>
            )}
            {(() => {
              const c = resp.constraints || {};
              const chips = [];
              if (c.dim_min_cm != null && c.dim_max_cm != null) chips.push(`${c.dim_min_cm}–${c.dim_max_cm} cm`);
              else if (c.dim_max_cm != null) chips.push(`max ${c.dim_max_cm} cm`);
              else if (c.dim_min_cm != null) chips.push(`min ${c.dim_min_cm} cm`);
              if (c.price_min != null && c.price_max != null) chips.push(`${Number(c.price_min).toLocaleString("it-IT")}–${Number(c.price_max).toLocaleString("it-IT")} €`);
              else if (c.price_max != null) chips.push(`≤ ${Number(c.price_max).toLocaleString("it-IT")} €`);
              if (c.environment === "outdoor") chips.push("Outdoor · esterno");
              if (c.environment === "indoor") chips.push("Indoor · interno");
              return chips.length > 0 ? (
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="preventivatore-constraints">
                  {chips.map((ch) => (
                    <span key={ch} className="text-[9px] tracking-[0.12em] uppercase bg-gold/10 border border-gold/40 text-gold px-1.5 py-0.5 rounded-sm font-semibold">
                      {ch}
                    </span>
                  ))}
                </div>
              ) : null;
            })()}
          </div>
        )}

        {/* Risultati AI */}
        {!loading && resp?.results?.length > 0 && (
          <div className="space-y-2" data-testid="preventivatore-results">
            {resp.results.map((r, i) => (
              <div
                key={r.asset_id}
                className="bg-cream/30 border border-ink/10 rounded-sm p-3 group"
                data-testid={`preventivatore-result-${i}`}
              >
                {/* Riga top: header info a sinistra, badge sconto/aumento a destra */}
                <div className="flex items-start justify-between gap-3 mb-1">
                  <div className="flex items-center gap-2 text-[9px] tracking-[0.2em] uppercase text-slate1 min-w-0">
                    <span className="font-display text-lg text-gold leading-none" data-testid={`preventivatore-result-rank-${i}`}>#{i + 1}</span>
                    {r.ai_score > 0 && (
                      <span className="text-[8px] tracking-[0.15em] uppercase text-slate1 bg-ink/5 px-1 rounded-sm">{r.ai_score}</span>
                    )}
                    <Building2 size={10} />
                    <span className="truncate">{r.supplier_name || "—"}</span>
                    <span className="text-ink/30">·</span>
                    <span className="text-gold">{r.kind}</span>
                    {r.suggested_page && (
                      <>
                        <span className="text-ink/30">·</span>
                        <span className="font-medium">AI: Pag. {r.suggested_page}</span>
                      </>
                    )}
                  </div>
                  <SupplierTermsBadges result={r} testidPrefix={`preventivatore-result-${i}`} />
                </div>
                {/* Titolo del file (cliccabile = apri pagina AI suggerita) */}
                <div className="flex items-center gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => onOpen(r)}
                    className="flex items-start gap-2 text-left flex-1 min-w-0 hover:text-gold transition-colors"
                    data-testid={`preventivatore-result-${i}-title-btn`}
                  >
                    <FileText size={12} className="text-slate1 shrink-0 mt-0.5" />
                    <div className="text-xs font-medium text-ink leading-snug">{r.title || r.filename}</div>
                    <ChevronRight size={14} className="text-slate1 shrink-0 mt-0.5 ml-auto" />
                  </button>
                  {onPhotoSearch && (
                    <button
                      type="button"
                      onClick={() => onPhotoSearch(r)}
                      className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-[0.15em] uppercase border border-gold/40 text-gold hover:bg-gold/10 rounded-sm transition-colors"
                      title="Cerca foto prodotto con AI sul sito del fornitore"
                      data-testid={`preventivatore-result-${i}-photo-ai-btn`}
                    >
                      <ImageIcon size={11} /> Foto AI
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onToggleFav(r, aiPage(r))}
                    className={`shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-sm border transition-colors ${favMap?.has(`${r.asset_id}:${aiPage(r)}`) ? "border-gold bg-gold/15 text-gold" : "border-ink/15 text-slate1 hover:border-gold hover:text-gold"}`}
                    title={favMap?.has(`${r.asset_id}:${aiPage(r)}`) ? "Rimuovi dai preferiti" : "Salva nei preferiti"}
                    data-testid={`preventivatore-result-${i}-fav`}
                  >
                    <Star size={13} className={favMap?.has(`${r.asset_id}:${aiPage(r)}`) ? "fill-gold" : ""} />
                  </button>
                  <button
                    type="button"
                    onClick={() => on3D(r)}
                    className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-[0.15em] uppercase border border-ink/15 text-slate1 hover:border-gold hover:text-ink rounded-sm transition-colors"
                    title="File 3D / CAD di questo fornitore"
                    data-testid={`preventivatore-result-${i}-3d`}
                  >
                    <Box size={11} /> 3D
                  </button>
                </div>
                {/* Miniature di tutte le pagine match — paginate 5 alla volta */}
                {r.snippets && r.snippets.length > 0 && (
                  <PagedMatches
                    items={r.snippets}
                    wrapperClassName="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 min-w-0"
                    testidPrefix={`preventivatore-result-${i}`}
                    renderItem={(s) => {
                      const pFav = favMap?.has(`${r.asset_id}:${s.page}`);
                      return (
                        <div key={`${r.asset_id}-${s.page}`} className="relative shrink-0">
                          <PageThumbnail
                            assetId={r.asset_id}
                            page={s.page}
                            pageLabel
                            snippet={s.snippet}
                            suggested={s.page === r.suggested_page}
                            testid={`preventivatore-result-${i}-thumb-${s.page}`}
                            onClick={() => onOpen({ ...r, suggested_page: s.page })}
                          />
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onToggleFav(r, s.page); }}
                            className={`absolute top-1 right-1 z-10 inline-flex items-center justify-center w-6 h-6 rounded-sm border shadow-sm transition-colors ${pFav ? "border-gold bg-gold text-white" : "border-white/70 bg-ink/55 text-white hover:bg-gold hover:border-gold"}`}
                            title={pFav ? "Rimuovi questa pagina dai preferiti" : "Salva questa pagina nei preferiti"}
                            data-testid={`preventivatore-result-${i}-page-${s.page}-fav`}
                          >
                            <Star size={12} className={pFav ? "fill-white" : ""} />
                          </button>
                        </div>
                      );
                    }}
                  />
                )}
                {/* AI reasoning per questo risultato */}
                {r.ai_reason && (
                  <div className="mt-2 text-[11px] text-slate1 italic leading-relaxed line-clamp-3">
                    <span className="text-gold not-italic">↳</span> {r.ai_reason}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {!loading && resp && resp.results?.length === 0 && (
          <EmptyBlock query={resp.query} hint="L'AI non ha trovato match rilevanti." />
        )}
        {!loading && !resp && (
          <div className="text-center text-slate1/60 text-xs py-8" data-testid="col-ai-idle">
            Inserisci una query per avviare la ricerca AI.
          </div>
        )}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// COLONNA DESTRA — Ricerca Tradizionale (no AI)
// ───────────────────────────────────────────────────────────────────────────
function ColumnTraditional({ loading, resp, onOpen, onPhotoSearch, favMap, onToggleFav, on3D }) {
  const tradPage = (r) => r.matches?.[0]?.page || 1;
  return (
    <div className="min-w-0 bg-white border border-ink/20 rounded-sm overflow-hidden" data-testid="col-traditional">
      {/* Header colonna */}
      <div className="bg-cream2/50 border-b border-ink/15 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="inline-flex items-center gap-2">
            <Filter size={14} className="text-ink" />
            <span className="text-[11px] tracking-[0.25em] uppercase text-ink font-medium">Ricerca tradizionale</span>
          </div>
          {resp?.total_matches !== undefined && (
            <span className="text-[10px] tracking-[0.15em] uppercase text-slate1">
              {resp.results.length} file · {resp.total_matches} match
            </span>
          )}
        </div>
        <div className="text-[9px] tracking-[0.15em] uppercase text-slate1/70 mt-1">
          AND su tutti i token nella stessa pagina · no AI · zero ambiguità
        </div>
      </div>

      <div className="p-4">
        {loading && <LoadingBlock label="Ricerca AND in tutti i listini…" />}

        {!loading && resp?.tokens && resp.tokens.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-1.5">
            {resp.tokens.map((tok) => (
              <span key={tok} className="text-[9px] tracking-[0.15em] uppercase bg-ink/5 border border-ink/10 px-1.5 py-0.5 rounded-sm text-ink">
                {tok}
              </span>
            ))}
          </div>
        )}

        {!loading && resp?.results?.length > 0 && (
          <div className="space-y-2" data-testid="traditional-results">
            {resp.results.map((r, i) => (
              <div
                key={r.asset_id}
                className="bg-cream/30 border border-ink/10 rounded-sm p-3"
                data-testid={`traditional-result-${i}`}
              >
                {/* Riga top: header info a sinistra, badge sconto/aumento a destra */}
                <div className="flex items-start justify-between gap-3 mb-1">
                  <div className="flex items-center gap-2 text-[9px] tracking-[0.2em] uppercase text-slate1 min-w-0">
                    <Building2 size={10} />
                    <span className="truncate">{r.supplier_name || "—"}</span>
                    <span className="text-ink/30">·</span>
                    <span className="text-gold">{r.kind}</span>
                    <span className="text-ink/30">·</span>
                    <span>{r.matches_count} match</span>
                  </div>
                  <SupplierTermsBadges result={r} testidPrefix={`traditional-result-${i}`} />
                </div>
                {/* Titolo del file — cliccabile: apre il listino. Se non ci sono
                    match (ricerca per solo brand) apre la prima pagina. */}
                <div className="flex items-center gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => onOpen(r, tradPage(r))}
                    className="flex items-start gap-2 text-left flex-1 min-w-0 hover:text-gold transition-colors"
                    data-testid={`traditional-result-${i}-title-btn`}
                  >
                    <FileText size={12} className="text-slate1 shrink-0 mt-0.5" />
                    <div className="text-xs font-medium text-ink leading-snug">{r.title || r.filename}</div>
                    <ChevronRight size={14} className="text-slate1 shrink-0 mt-0.5 ml-auto" />
                  </button>
                  {onPhotoSearch && (
                    <button
                      type="button"
                      onClick={() => onPhotoSearch(r)}
                      className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-[0.15em] uppercase border border-gold/40 text-gold hover:bg-gold/10 rounded-sm transition-colors"
                      title="Cerca foto prodotto con AI sul sito del fornitore"
                      data-testid={`traditional-result-${i}-photo-ai-btn`}
                    >
                      <ImageIcon size={11} /> Foto AI
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onToggleFav(r, tradPage(r))}
                    className={`shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-sm border transition-colors ${favMap?.has(`${r.asset_id}:${tradPage(r)}`) ? "border-gold bg-gold/15 text-gold" : "border-ink/15 text-slate1 hover:border-gold hover:text-gold"}`}
                    title={favMap?.has(`${r.asset_id}:${tradPage(r)}`) ? "Rimuovi dai preferiti" : "Salva nei preferiti"}
                    data-testid={`traditional-result-${i}-fav`}
                  >
                    <Star size={13} className={favMap?.has(`${r.asset_id}:${tradPage(r)}`) ? "fill-gold" : ""} />
                  </button>
                  <button
                    type="button"
                    onClick={() => on3D(r)}
                    className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-[0.15em] uppercase border border-ink/15 text-slate1 hover:border-gold hover:text-ink rounded-sm transition-colors"
                    title="File 3D / CAD di questo fornitore"
                    data-testid={`traditional-result-${i}-3d`}
                  >
                    <Box size={11} /> 3D
                  </button>
                </div>
                {/* Una miniatura per ogni pagina match — paginate 5 alla volta */}
                {r.matches && r.matches.length > 0 && (
                  <PagedMatches
                    items={r.matches}
                    wrapperClassName="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 min-w-0"
                    testidPrefix={`traditional-result-${i}`}
                    renderItem={(m) => {
                      const pFav = favMap?.has(`${r.asset_id}:${m.page}`);
                      return (
                        <div key={`${r.asset_id}-${m.page}`} className="relative shrink-0">
                          <PageThumbnail
                            assetId={r.asset_id}
                            page={m.page}
                            pageLabel
                            snippet={m.snippet}
                            testid={`traditional-result-${i}-thumb-${m.page}`}
                            onClick={() => onOpen(r, m.page)}
                          />
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onToggleFav(r, m.page); }}
                            className={`absolute top-1 right-1 z-10 inline-flex items-center justify-center w-6 h-6 rounded-sm border shadow-sm transition-colors ${pFav ? "border-gold bg-gold text-white" : "border-white/70 bg-ink/55 text-white hover:bg-gold hover:border-gold"}`}
                            title={pFav ? "Rimuovi questa pagina dai preferiti" : "Salva questa pagina nei preferiti"}
                            data-testid={`traditional-result-${i}-page-${m.page}-fav`}
                          >
                            <Star size={12} className={pFav ? "fill-white" : ""} />
                          </button>
                        </div>
                      );
                    }}
                  />
                )}
                {/* Snippet di anteprima del primo match */}
                {r.matches && r.matches[0] && (
                  <div className="mt-2 text-[11px] text-ink/70 bg-cream2/40 px-2 py-1 rounded-sm leading-relaxed line-clamp-2">
                    {r.matches[0].snippet}
                  </div>
                )}
                {r.title_match && r.matches.length === 0 && (
                  <button
                    onClick={() => onOpen(r, null)}
                    className="mt-2 text-[10px] tracking-[0.15em] uppercase bg-white hover:bg-gold hover:text-cream border border-gold/40 hover:border-gold px-2 py-0.5 rounded-sm text-ink transition-colors inline-flex items-center gap-1"
                    data-testid={`traditional-result-${i}-titleonly`}
                  >
                    Apri (match nel titolo)
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {!loading && resp && resp.results?.length === 0 && (
          <EmptyBlock query={resp.query} hint="Nessuna pagina contiene TUTTE le parole insieme." />
        )}
        {!loading && !resp && (
          <div className="text-center text-slate1/60 text-xs py-8" data-testid="col-traditional-idle">
            Inserisci una query per avviare la ricerca tradizionale.
          </div>
        )}
      </div>
    </div>
  );
}

function LoadingBlock({ label }) {
  return (
    <div className="flex items-center justify-center gap-2 py-8 text-slate1 text-xs">
      <Loader2 size={14} className="animate-spin" /> {label}
    </div>
  );
}

// Badge angolo-superiore-destro per card risultato Preventivatore: mostra
// "ACQUISTO -40+10+5%" (verde scuro) e "+5% dal 01/03/26" (rosso scuro)
// se il fornitore ha sconto acquisto e/o aumento listino programmato.
// Gli aumenti vengono mostrati solo se l'asset NON è esente (applies_price_increase=true).
// PRIVACY: lo sconto acquisto è NASCOSTO di default (icona occhio) — si rivela
// solo cliccando il badge, per non mostrarlo a schermo davanti ai clienti.
function SupplierTermsBadges({ result, testidPrefix }) {
  const [showDiscount, setShowDiscount] = React.useState(false);
  const hasDiscount = !!result.purchase_discount_label;
  const hasIncrease = !!(result.applies_price_increase && (result.price_increase_pct > 0 || result.price_increase_new_listino));
  const isStale = Boolean(result.asset_stale);
  if (!hasDiscount && !hasIncrease && !isStale) return null;
  const formatDate = (iso) => {
    if (!iso) return "";
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1].slice(2)}` : iso;
  };
  return (
    <div className="flex flex-col items-end gap-1 shrink-0">
      {hasDiscount && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setShowDiscount((v) => !v); }}
          className="bg-emerald-900/90 text-cream px-2 py-1 rounded-sm border border-emerald-700/70 leading-none cursor-pointer hover:bg-emerald-800 transition-colors"
          title={showDiscount ? "Clicca per nascondere lo sconto acquisto" : `Clicca per vedere lo sconto acquisto ${result.supplier_name || ""}`}
          data-testid={`${testidPrefix}-discount-badge`}
        >
          <div className="text-[7px] tracking-[0.25em] uppercase opacity-70 flex items-center gap-1">
            Acquisto {showDiscount ? <EyeOff size={8} /> : null}
          </div>
          {showDiscount ? (
            <div className="text-xs font-semibold mt-0.5" data-testid={`${testidPrefix}-discount-value`}>
              −{result.purchase_discount_label}
            </div>
          ) : (
            <div className="flex items-center justify-end mt-0.5" data-testid={`${testidPrefix}-discount-hidden`}>
              <Eye size={13} />
            </div>
          )}
        </button>
      )}
      {hasIncrease && (
        <div
          className="bg-rose-900/90 text-cream px-2 py-1 rounded-sm border border-rose-700/70 leading-none"
          title={
            result.price_increase_date
              ? (result.price_increase_new_listino
                  ? `Nuovo listino in vigore dal ${formatDate(result.price_increase_date)}`
                  : `Aumento listino in vigore dal ${formatDate(result.price_increase_date)}`)
              : (result.price_increase_new_listino ? "Nuovo listino programmato" : "Aumento listino programmato")
          }
          data-testid={`${testidPrefix}-increase-badge`}
        >
          <div className="text-[7px] tracking-[0.25em] uppercase opacity-70">
            {result.price_increase_date ? `Dal ${formatDate(result.price_increase_date)}` : "Aumento"}
          </div>
          <div className="text-xs font-semibold mt-0.5" data-testid={`${testidPrefix}-increase-value`}>
            {result.price_increase_new_listino
              ? "Nuovo listino"
              : <>+{Number(result.price_increase_pct).toFixed(result.price_increase_pct % 1 === 0 ? 0 : 1)}%</>}
          </div>
        </div>
      )}
      {isStale && (
        <div
          className="bg-amber-500/95 text-ink px-2 py-1 rounded-sm border border-amber-600/70 leading-none animate-pulse"
          title={`Listino caricato oltre 2 mesi fa${result.asset_uploaded_at ? ` (${formatDate(result.asset_uploaded_at)})` : ""} — verifica che sia ancora valido`}
          data-testid={`${testidPrefix}-stale-badge`}
        >
          <div className="text-[7px] tracking-[0.25em] uppercase opacity-70">
            {result.asset_uploaded_at ? `File del ${formatDate(result.asset_uploaded_at)}` : "Listino"}
          </div>
          <div className="text-xs font-semibold mt-0.5" data-testid={`${testidPrefix}-stale-value`}>
            ⚠ Da verificare
          </div>
        </div>
      )}
    </div>
  );
}
// Mini preview della pagina PDF (~96x136 px). Caricamento lazy: fetch parte solo
// quando la thumb entra nel viewport (IntersectionObserver). Token via header
// Authorization perché `<img>` non lo invia automaticamente — serviamo invece
// l'immagine via blob URL dopo fetch autenticato.
//
// Props extra:
//   - pageLabel: se true mostra badge "PAG. N" in basso sull'immagine
//   - snippet: testo che appare come tooltip nativo al passaggio del mouse
//   - suggested: se true evidenzia il bordo in oro (pagina scelta dall'AI)
// Griglia/lista di match paginata: mostra `step` elementi per volta con un
// pulsante "+ Altre pagine" finché ci sono match. Usata per le miniature delle
// pagine in Preventivatore e Magic Search (prima erano fisse a 5).
export function PagedMatches({ items = [], step = 5, wrapperClassName = "", testidPrefix, renderItem }) {
  const [visible, setVisible] = React.useState(step);
  React.useEffect(() => { setVisible(step); }, [items, step]);
  if (!items || items.length === 0) return null;
  const shown = items.slice(0, visible);
  const remaining = items.length - visible;
  return (
    <div className="min-w-0">
      <div className={wrapperClassName} data-testid={testidPrefix ? `${testidPrefix}-thumbs` : undefined}>
        {shown.map((it, idx) => renderItem(it, idx))}
      </div>
      {remaining > 0 && (
        <div className="flex items-center gap-2 mt-1.5">
          <button
            type="button"
            onClick={() => setVisible((v) => v + step)}
            className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase border border-ink/20 hover:border-gold hover:text-gold text-slate1 px-2.5 py-1 rounded-sm transition-colors"
            data-testid={testidPrefix ? `${testidPrefix}-more` : undefined}
          >
            + Altre {Math.min(step, remaining)} pagine (ne restano {remaining})
          </button>
          <button
            type="button"
            onClick={() => setVisible(items.length)}
            className="text-[10px] tracking-[0.15em] uppercase text-slate1 hover:text-ink underline underline-offset-2"
            data-testid={testidPrefix ? `${testidPrefix}-all` : undefined}
          >
            Tutte ({items.length})
          </button>
        </div>
      )}
    </div>
  );
}

export function PageThumbnail({ assetId, page, testid, onClick, pageLabel, snippet, suggested }) {
  const [src, setSrc] = React.useState("");
  const [error, setError] = React.useState(false);
  const [hover, setHover] = React.useState(false);
  const [zoomSrc, setZoomSrc] = React.useState("");
  const [zoomLoading, setZoomLoading] = React.useState(false);
  const [zoomPos, setZoomPos] = React.useState({ side: "right", top: 0, left: 0 });
  const ref = React.useRef(null);
  const loadedRef = React.useRef(false);
  const zoomLoadedRef = React.useRef(false);

  React.useEffect(() => {
    if (!page || !assetId) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(async (entries) => {
      if (loadedRef.current) return;
      if (!entries.some((e) => e.isIntersecting)) return;
      loadedRef.current = true;
      // Una micro-retry: lo stesso endpoint a volte fallisce sotto carico
      // (rasterizzazione PDF concorrente). Riproviamo una volta con backoff.
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const { data } = await api.get(`/assets/${assetId}/thumbnail`, {
            params: { page, width: 200 },
            responseType: "blob",
            timeout: 25000,
          });
          const url = URL.createObjectURL(data);
          setSrc(url);
          setError(false);
          return;
        } catch (_) {
          if (attempt === 0) {
            // backoff: 600ms prima di riprovare
            await new Promise((r) => setTimeout(r, 600));
          }
        }
      }
      setError(true);
    }, { rootMargin: "200px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [assetId, page]);

  // Lazy-load della versione ad alta risoluzione (480px) al primo hover.
  // Mostriamo il thumbnail piccolo intanto che carica il grande.
  const loadZoom = React.useCallback(async () => {
    if (zoomLoadedRef.current || !page || !assetId) return;
    zoomLoadedRef.current = true;
    setZoomLoading(true);
    try {
      const { data } = await api.get(`/assets/${assetId}/thumbnail`, {
        params: { page, width: 480 },
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      setZoomSrc(url);
    } catch (_) {
      // Fallback: usiamo il thumbnail piccolo se la versione grande fallisce
    } finally {
      setZoomLoading(false);
    }
  }, [assetId, page]);

  // Cleanup blob URLs on unmount
  React.useEffect(() => () => {
    if (src) URL.revokeObjectURL(src);
    if (zoomSrc) URL.revokeObjectURL(zoomSrc);
  }, [src, zoomSrc]);

  const handleEnter = (e) => {
    if (!page) return;
    setHover(true);
    loadZoom();
    // Decidi se popup va a destra (default) o a sinistra (se non c'è spazio)
    const rect = e.currentTarget.getBoundingClientRect();
    const popupWidth = 300;
    const margin = 8;
    const viewportW = window.innerWidth;
    const side = (rect.right + margin + popupWidth > viewportW) ? "left" : "right";
    setZoomPos({ side, top: rect.top, left: side === "right" ? rect.right + margin : rect.left - margin - popupWidth });
  };
  const handleLeave = () => setHover(false);

  const baseClass = "shrink-0 w-[96px] h-[136px] bg-cream2/60 border rounded-sm overflow-hidden relative flex items-center justify-center";
  const borderClass = suggested ? "border-gold border-2 shadow-[0_0_0_1px_rgba(201,169,120,0.25)]" : "border-ink/15";
  const titleAttr = snippet || (page ? `Apri PDF a pag. ${page}` : undefined);
  const pageBadge = pageLabel && page ? (
    <span className={`absolute bottom-0 left-0 right-0 ${suggested ? "bg-gold text-ink" : "bg-ink/85 text-cream"} text-[9px] tracking-[0.2em] text-center py-0.5 font-medium`}>
      {suggested ? "★ AI · " : ""}PAG. {page}
    </span>
  ) : null;

  // Popup zoom: visualizzato in posizione FIXED ancorato alla miniatura.
  // 300×420 (≈3× la dimensione del thumb originale, niente fastidio per il
  // mouse perché è offset di 8px). Anteprima rapida senza dover aprire il PDF.
  const zoomPopup = hover && page ? (
    <div
      className="fixed z-[9999] pointer-events-none animate-in fade-in duration-150"
      style={{
        top: Math.max(8, Math.min(zoomPos.top, window.innerHeight - 432)),
        left: zoomPos.left,
        width: 300,
      }}
      data-testid={testid ? `${testid}-zoom` : undefined}
    >
      <div className="bg-white border border-ink/20 rounded-sm shadow-2xl overflow-hidden">
        <div className="w-[300px] h-[420px] bg-cream2 flex items-center justify-center relative">
          {zoomLoading && !zoomSrc && (
            <Loader2 size={20} className="text-slate1/40 animate-spin" />
          )}
          {(zoomSrc || src) && (
            <img
              src={zoomSrc || src}
              alt={`Pag. ${page} ingrandita`}
              className="w-full h-full object-contain"
              draggable={false}
            />
          )}
          <span className={`absolute bottom-0 left-0 right-0 ${suggested ? "bg-gold text-ink" : "bg-ink/85 text-cream"} text-[10px] tracking-[0.25em] text-center py-1 font-medium`}>
            {suggested ? "★ AI · " : ""}PAG. {page}
          </span>
        </div>
      </div>
    </div>
  ) : null;

  if (!page) {
    return <div ref={ref} className={`${baseClass} ${borderClass}`} data-testid={testid}><FileText size={18} className="text-slate1/40" /></div>;
  }
  // Stato di errore: l'anteprima è fallita ma la pagina esiste — manteniamo
  // il pulsante cliccabile per permettere comunque di aprire il PDF a quella
  // pagina (la rasterizzazione thumbnail può fallire sotto carico anche se
  // il PDF apre normalmente nel viewer).
  if (error) {
    const errorContent = (
      <>
        <FileText size={18} className="text-rose-300" />
        {pageBadge}
      </>
    );
    if (onClick) {
      return (
        <button
          ref={ref}
          type="button"
          onClick={(e) => { e.stopPropagation(); onClick(); }}
          className={`${baseClass} ${borderClass} cursor-pointer hover:border-gold transition-all`}
          data-testid={testid}
          title={`Anteprima non disponibile · click per aprire PDF a pag. ${page}`}
        >
          {errorContent}
        </button>
      );
    }
    return (
      <div ref={ref} className={`${baseClass} ${borderClass}`} data-testid={testid}>
        {errorContent}
      </div>
    );
  }
  if (!src) {
    return (
      <div ref={ref} className={`${baseClass} ${borderClass}`} data-testid={testid}>
        <Loader2 size={14} className="text-slate1/40 animate-spin" />
        {pageBadge}
      </div>
    );
  }
  const img = (
    <img
      src={src}
      alt={`Pag. ${page}`}
      loading="lazy"
      className="w-full h-full object-cover"
    />
  );
  if (onClick) {
    return (
      <>
        <button
          ref={ref}
          type="button"
          onClick={(e) => { e.stopPropagation(); onClick(); }}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          onFocus={handleEnter}
          onBlur={handleLeave}
          className={`${baseClass} ${borderClass} cursor-pointer hover:border-gold hover:shadow-sm transition-all`}
          data-testid={testid}
          title={titleAttr}
        >
          {img}
          {pageBadge}
        </button>
        {zoomPopup}
      </>
    );
  }
  return (
    <>
      <div
        ref={ref}
        className={`${baseClass} ${borderClass}`}
        data-testid={testid}
        title={titleAttr}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        {img}
        {pageBadge}
      </div>
      {zoomPopup}
    </>
  );
}

function EmptyBlock({ query, hint }) {
  return (
    <div className="text-center py-8 text-slate1">
      <Search size={24} className="mx-auto mb-2 text-slate1/40" />
      <div className="text-xs">Nessun risultato per &ldquo;{query}&rdquo;.</div>
      <div className="text-[10px] mt-1 text-slate1/70">{hint}</div>
    </div>
  );
}
